import { type ReactNode, useState } from 'react';

import {
  CopyPlus,
  Loader2,
  MessageSquarePlus,
  RefreshCw,
  TriangleAlert,
} from 'lucide-react';

import { BetaBadge } from 'components/BetaBadge';
import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { SplitButton } from 'components/SplitButton';
import { Flex } from 'components/layout/Flex';
import { Typography } from 'components/typography';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';

import {
  LoadCloneJobController,
  LoadCloneJobStateStatus,
  canRetryCloneJob,
  hasUnconfirmedCloneCreation,
} from '../useLoadCloneJob';
import InstructionsDialog from './InstructionsDialog';
import JobCard from './JobCard';

type TaskCardProps = LoadCloneJobController & {
  sourceLoadId: number;
  emailId: number;
  onOpenLoad: (loadIdentifier: string) => void;
  idlePresentation?: ReactNode;
};

export default function TaskCard({
  state,
  sourceLoadId,
  emailId,
  start,
  refresh,
  onOpenLoad,
  idlePresentation,
}: TaskCardProps) {
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);

  if (state.status === LoadCloneJobStateStatus.Disabled) {
    return null;
  }

  if (state.status === LoadCloneJobStateStatus.Discovering) {
    return <CloneEntryPoint discovering />;
  }

  if (state.status === LoadCloneJobStateStatus.Idle) {
    if (idlePresentation) return <>{idlePresentation}</>;
    return (
      <div className='space-y-1.5'>
        <CloneEntryPoint
          onClick={() => void start()}
          onAddInstructions={() => setIsInstructionsOpen(true)}
        />
        {state.startError && !isInstructionsOpen && (
          <Typography
            variant='body-xs'
            className='leading-4 text-error-600'
            role='alert'
          >
            {state.startError.message}
          </Typography>
        )}
        <InstructionsDialog
          sourceLoadId={sourceLoadId}
          emailId={emailId}
          open={isInstructionsOpen}
          startError={state.startError}
          onOpenChange={setIsInstructionsOpen}
          onStart={start}
        />
      </div>
    );
  }

  if (state.status === LoadCloneJobStateStatus.DiscoveryError) {
    return (
      <Card className='bg-neutral-50 p-3 shadow-none'>
        <Flex className='gap-2.5'>
          <TriangleAlert
            className='mt-0.5 h-4 w-4 shrink-0 text-warning-600'
            aria-hidden='true'
          />
          <div className='min-w-0 flex-1'>
            <Typography variant='body-sm' weight='medium' className='leading-5'>
              Could not check clone status
            </Typography>
            <Typography
              variant='body-xs'
              textColor='lessMuted'
              className='mt-0.5 leading-4'
            >
              {state.error.message}
            </Typography>
            <Button
              buttonNamePosthog={null}
              variant='ghost'
              size='auto'
              className='mt-2 h-auto gap-1.5 px-0 py-1 text-xs text-brand-main hover:border-transparent'
              onClick={refresh}
            >
              <RefreshCw className='h-3.5 w-3.5' aria-hidden='true' />
              Refresh status
            </Button>
          </div>
        </Flex>
      </Card>
    );
  }

  if (
    state.status === LoadCloneJobStateStatus.RecommendationReconciliationError
  ) {
    return (
      <Card className='bg-neutral-50 p-3 shadow-none'>
        <Typography variant='body-sm' weight='medium'>
          Could not refresh recommendation
        </Typography>
        <Typography variant='body-xs' textColor='lessMuted' className='mt-1'>
          {state.error.message}
        </Typography>
        <Button
          buttonNamePosthog={null}
          variant='ghost'
          size='auto'
          className='mt-2 h-auto gap-1.5 px-0 py-1 text-xs text-brand-main hover:border-transparent'
          onClick={refresh}
        >
          <RefreshCw className='h-3.5 w-3.5' aria-hidden='true' />
          Retry recommendation refresh
        </Button>
      </Card>
    );
  }

  if (state.status === LoadCloneJobStateStatus.Starting) {
    return <ActiveTaskCard title='Starting load cloning...' />;
  }

  if (state.status === LoadCloneJobStateStatus.RefreshingRecommendation) {
    return <ActiveTaskCard title='Refreshing recommendation...' />;
  }

  if (state.status === LoadCloneJobStateStatus.StartedWithoutJob) {
    return (
      <Card className='bg-neutral-50 p-3 shadow-none' role='status'>
        <Typography variant='body-sm' weight='medium'>
          Load cloning was started
        </Typography>
        <Typography variant='body-xs' textColor='lessMuted' className='mt-1'>
          Its saved progress is no longer available. Check the TMS before taking
          another action.
        </Typography>
      </Card>
    );
  }

  const retryable = canRetryCloneJob(state.job);
  return (
    <Flex direction='col' align='stretch' gap='sm'>
      <JobCard
        job={state.job}
        refreshError={state.refreshError?.message}
        onRefresh={refresh}
        onOpenLoad={onOpenLoad}
      />
      {hasUnconfirmedCloneCreation(state.job) && (
        <Typography variant='body-xs' textColor='lessMuted'>
          Creation wasn’t confirmed. Check the TMS and contact support before
          retrying this email.
        </Typography>
      )}
      {retryable && (
        <Flex direction='col' align='stretch' gap='sm'>
          <Typography variant='body-xs' textColor='lessMuted'>
            Retry this email with the same source load. Existing POs will be
            skipped.
          </Typography>
          {state.startError && !isInstructionsOpen && (
            <Typography
              variant='body-xs'
              role='alert'
              className='text-error-600'
            >
              {state.startError.message}
            </Typography>
          )}
          <Flex gap='sm' wrap='wrap'>
            <Button
              buttonNamePosthog={ButtonNamePosthog.CloneLoad}
              size='sm'
              disabled={state.isRetrying}
              onClick={() => void start()}
            >
              <Typography
                variant='body-sm'
                weight='medium'
                className='text-inherit'
              >
                {state.isRetrying ? 'Retrying...' : 'Try again'}
              </Typography>
            </Button>
            <Button
              buttonNamePosthog={ButtonNamePosthog.OpenLoadCloneInstructions}
              size='sm'
              variant='outline'
              disabled={state.isRetrying}
              onClick={() => setIsInstructionsOpen(true)}
            >
              <Typography
                variant='body-sm'
                weight='medium'
                className='text-inherit'
              >
                Edit instructions and retry
              </Typography>
            </Button>
          </Flex>
          <InstructionsDialog
            retry
            sourceLoadId={sourceLoadId}
            emailId={emailId}
            open={isInstructionsOpen}
            startError={state.startError}
            onOpenChange={setIsInstructionsOpen}
            onStart={start}
          />
        </Flex>
      )}
    </Flex>
  );
}

enum CloneMode {
  Immediate = 'immediate',
  Review = 'review',
}

const cloneOptions = [
  {
    mode: CloneMode.Immediate,
    label: 'Clone Load with AI',
    helper: 'Kit will infer everything from the email',
    tooltip:
      'Load cloning is in beta. Kit runs this in the background, so you can keep working or close Drumkit while it finishes. Re-open this load to check back on the progress of the clones.',
    Icon: CopyPlus,
  },
  {
    mode: CloneMode.Review,
    label: 'Clone Load and Review AI Instructions',
    helper: 'Check and edit before Kit clones',
    tooltip:
      'Load cloning is in beta. Edit saved customer instructions before starting. Changes apply to future runs. This is not a preview of the loads Kit will create.',
    Icon: MessageSquarePlus,
  },
];

export function CloneEntryPoint({
  discovering = false,
  onClick,
  onAddInstructions,
}: {
  discovering?: boolean;
  onClick?: () => void;
  onAddInstructions?: () => void;
}) {
  const [mode, setMode] = useState(CloneMode.Immediate);
  const selected = cloneOptions.find((option) => option.mode === mode)!;
  const eventFor = (action: CloneMode) =>
    action === CloneMode.Immediate
      ? ButtonNamePosthog.CloneLoad
      : ButtonNamePosthog.OpenLoadCloneInstructions;
  const run = (nextMode: CloneMode) => {
    setMode(nextMode);
    if (nextMode === CloneMode.Review) onAddInstructions?.();
    else onClick?.();
  };
  return (
    <SplitButton
      variant='secondary'
      size='sm'
      disabled={discovering}
      primaryAriaLabel={selected.label}
      primaryBusy={discovering}
      primaryTestId='clone-load-button'
      primaryTooltip={selected.tooltip}
      primaryButtonNamePosthog={eventFor(mode)}
      onPrimaryClick={() => run(mode)}
      menuTriggerAriaLabel='More load cloning options'
      className='items-stretch'
      primaryClassName='gap-2 text-sm'
      segmentStyle={{
        height: 'auto',
        minHeight: 36,
        whiteSpace: 'normal',
        paddingBlock: 8,
      }}
      menuContentStyle={{ width: 320, maxWidth: 'calc(100vw - 24px)' }}
      primaryLabel={
        <>
          <selected.Icon size={16} className='shrink-0' aria-hidden='true' />
          <Typography
            variant='button'
            weight='medium'
            as='span'
            className='min-w-0 text-left text-inherit'
          >
            {selected.label}
          </Typography>
          <BetaBadge />
        </>
      }
      menuItems={cloneOptions.map((option) => ({
        id: option.mode,
        label: option.label,
        ariaLabel: option.label,
        description: option.helper,
        icon: (
          <option.Icon
            className='mt-0.5 h-4 w-4 shrink-0 text-neutral-600'
            aria-hidden='true'
          />
        ),
        trailing: <BetaBadge />,
        buttonNamePosthog: eventFor(option.mode),
        onSelect: () => run(option.mode),
      }))}
    />
  );
}

function ActiveTaskCard({ title }: { title: string }) {
  return (
    <Card className='bg-neutral-50 p-3 shadow-none' role='status'>
      <Flex className='gap-2.5' aria-live='polite'>
        <Loader2
          className='mt-0.5 h-4 w-4 shrink-0 animate-spin text-brand-main motion-reduce:animate-none'
          aria-hidden='true'
        />
        <div className='min-w-0'>
          <Typography variant='body-sm' weight='medium' className='leading-5'>
            {title}
          </Typography>
          <Typography
            variant='body-xs'
            textColor='lessMuted'
            className='mt-0.5 leading-4'
          >
            You can work elsewhere while this continues.
          </Typography>
        </div>
      </Flex>
    </Card>
  );
}
